import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { listAccounts } from '../accounts/api';
import { ApiError } from '../../lib/api';
import { datetimeLocalToIso } from '../../lib/sydneyDateTime';
import { slugify } from '../../lib/slugify';
import { createPlan } from './api';

// Consolidation §2.1: no Status field on the create form — a new Plan is
// always Draft. Status moves through the detail-page status pill afterwards.

export function PlanNew() {
  const [params] = useSearchParams();
  const brandFromUrl = params.get('brand') ?? '';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const brands = (accounts.data ?? []).filter((a) => a.accountType === 'brand');

  const [name, setName] = useState('');
  const [overview, setOverview] = useState('');
  const [brandAccountId, setBrandAccountId] = useState(brandFromUrl);
  const [promoStartDate, setPromoStartDate] = useState('');
  const [promoEndDate, setPromoEndDate] = useState('');
  const [offerMechanicsUrl, setOfferMechanicsUrl] = useState('');
  const [kyckCampaignOverviewUrl, setKyckCampaignOverviewUrl] = useState('');
  // Rollout slug auto-derives from the name until the AM edits it directly
  // (Consolidation §2.4). monthYearLabel + notes are optional free text;
  // defaultCampaignId is always None on create (no Campaigns attached yet).
  const [rolloutSlug, setRolloutSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [monthYearLabel, setMonthYearLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onNameChange = (next: string) => {
    setName(next);
    if (!slugTouched) setRolloutSlug(slugify(next));
  };

  // Spec smoke test 20: end < start warns but does NOT block save.
  const dateOrderWarning =
    promoStartDate && promoEndDate && promoEndDate < promoStartDate
      ? 'End date is before start date — saving anyway.'
      : null;

  const mut = useMutation({
    mutationFn: async () => {
      setError(null);
      return createPlan({
        name: name.trim(),
        brandAccountId,
        overview: overview.trim() || null,
        promoStartDate: datetimeLocalToIso(promoStartDate),
        promoEndDate: datetimeLocalToIso(promoEndDate),
        offerMechanicsUrl: offerMechanicsUrl.trim() || null,
        kyckCampaignOverviewUrl: kyckCampaignOverviewUrl.trim() || null,
        rolloutSlug: rolloutSlug.trim() || null,
        monthYearLabel: monthYearLabel.trim() || null,
        notes: notes.trim() || null,
        defaultCampaignId: null,
      });
    },
    onSuccess: async (plan) => {
      await qc.invalidateQueries({ queryKey: ['plans'] });
      navigate(`/plans/${plan.id}`);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  if (accounts.isLoading) return <CenteredSpinner />;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">New plan</h1>
        <p className="text-sm text-slate-600">
          A Promotion Plan organises pre-launch work for a single brand. The
          Rollout is spawned from it later.
        </p>
      </div>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          // Spec smoke test 19: no brand blocks submission.
          if (!name.trim()) {
            setError('Name is required.');
            return;
          }
          if (!brandAccountId) {
            setError('Brand is required.');
            return;
          }
          mut.mutate();
        }}
      >
        <Field label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="May 2026 — Get Started for $1"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Overview">
          <textarea
            value={overview}
            onChange={(e) => setOverview(e.target.value)}
            rows={4}
            placeholder="Brief description of this promo... (optional)"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Brand" required>
          <Combobox
            aria-label="Brand"
            placeholder="— Select a brand —"
            searchPlaceholder="Search brands…"
            value={brandAccountId}
            onValueChange={setBrandAccountId}
            options={[
              { value: '', label: '— Select a brand —' },
              ...brands.map((b) => ({ value: b.id, label: b.name })),
            ]}
          />
        </Field>

        <Field label="Rollout slug (optional, derived from name)">
          <input
            type="text"
            value={rolloutSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setRolloutSlug(e.target.value);
            }}
            placeholder="snap-au-may-2026"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Promo start datetime">
            <input
              type="datetime-local"
              value={promoStartDate}
              onChange={(e) => setPromoStartDate(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Promo end datetime">
            <input
              type="datetime-local"
              value={promoEndDate}
              onChange={(e) => setPromoEndDate(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <p className="-mt-2 text-xs text-slate-500">
          Stored in Sydney time (AEST +10:00).
        </p>
        {dateOrderWarning && (
          <p className="text-sm text-amber-700">{dateOrderWarning}</p>
        )}

        <Field label="Month-year label">
          <input
            type="text"
            value={monthYearLabel}
            onChange={(e) => setMonthYearLabel(e.target.value)}
            placeholder="May 26"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">
            Substituted into <code className="font-mono">{'{monthYear}'}</code>{' '}
            in Meta naming and Google ad-label templates. Left blank, it's
            derived from the promo start month at Rollout creation.
          </p>
        </Field>

        <Field label="Offer mechanics URL">
          <input
            type="url"
            value={offerMechanicsUrl}
            onChange={(e) => setOfferMechanicsUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Kyck Campaign Overview URL">
          <input
            type="url"
            value={kyckCampaignOverviewUrl}
            onChange={(e) => setKyckCampaignOverviewUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Default Campaign (optional, must be attached first)">
          <select
            value=""
            disabled
            title="Attach Campaigns after creating the Plan, then set the default on the edit form."
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
          >
            <option value="">— None (rows start unassigned) —</option>
          </select>
          <p className="mt-1 text-xs text-slate-500">
            No Campaigns are attached yet. Attach Campaigns after creating the
            Plan, then choose a default on the edit form.
          </p>
        </Field>

        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Execution-context notes (optional)"
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create plan'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </span>
      {children}
    </label>
  );
}
